import React, { useEffect, useMemo, useState } from 'react'
import { Plus, Trash2, Edit2, Layers, GitBranch, ChevronDown, ChevronRight, X, ArrowUp, ArrowDown, Filter } from 'lucide-react'
import toast from 'react-hot-toast'
import {
  Question,
  SurveyPage,
  getQuestionAnswerPathOptions,
  getQuestionAnswerValueOptions,
  getQuestionReferenceLabel,
  normalizeQuestionPageState,
} from './QuestionBuilder'
import RichTextEditor from './RichTextEditor'

interface PageManagerProps {
  pages: SurveyPage[]
  questions: Question[]
  onPagesChange: (pages: SurveyPage[]) => void
  onQuestionsChange: (questions: Question[]) => void
  onClose: () => void
}

type PageBranchComparisonOperator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'not_contains'
  | 'greater_than'
  | 'less_than'
  | 'greater_or_equal'
  | 'less_or_equal'
  | 'is_answered'
  | 'not_answered'

const PAGE_BRANCH_COMPARISON_OPTIONS: Array<{ value: PageBranchComparisonOperator; label: string }> = [
  { value: 'equals', label: 'Resposta for igual a' },
  { value: 'not_equals', label: 'Resposta for diferente de' },
  { value: 'contains', label: 'Resposta contiver o valor' },
  { value: 'not_contains', label: 'Resposta nao contiver o valor' },
  { value: 'greater_than', label: 'Resposta for maior que' },
  { value: 'less_than', label: 'Resposta for menor que' },
  { value: 'greater_or_equal', label: 'Resposta for maior ou igual a' },
  { value: 'less_or_equal', label: 'Resposta for menor ou igual a' },
  { value: 'is_answered', label: 'Pergunta estiver respondida' },
  { value: 'not_answered', label: 'Pergunta estiver vazia' },
]

const isPresenceComparison = (comparison?: string) => comparison === 'is_answered' || comparison === 'not_answered'

interface PageBranchConditionDraft {
  questionId: string
  answerPath?: string
  answerValue: string
  comparison?: PageBranchComparisonOperator
  operator?: 'AND' | 'OR'
}

interface PageBranchDraft {
  id: string
  targetPageId: string
  targetQuestionId: string
  targetType: 'page' | 'question' | 'complete' | 'cancel_filter'
  conditions: PageBranchConditionDraft[]
  isMultiple: boolean
  unconditional: boolean
}

const generateBranchId = () => `page_branch_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`

const getQuestionOptions = (question?: Question) => question?.options || []

const questionHasOptions = (question: Question) =>
  getQuestionOptions(question).length > 0 || getQuestionAnswerPathOptions(question).length > 0

const stripHtml = (html: string) => {
  const tmp = document.createElement('div')
  tmp.innerHTML = html
  return tmp.textContent || tmp.innerText || ''
}

const PageManager: React.FC<PageManagerProps> = ({
  pages,
  questions,
  onPagesChange,
  onQuestionsChange,
  onClose
}) => {
  const normalizedState = useMemo(
    () => normalizeQuestionPageState(questions, pages),
    [questions, pages]
  )

  const normalizedQuestions = normalizedState.questions
  const normalizedPages = normalizedState.pages

  const [expandedPages, setExpandedPages] = useState<Set<string>>(new Set())
  const [editingPage, setEditingPage] = useState<SurveyPage | null>(null)
  const [branchPage, setBranchPage] = useState<SurveyPage | null>(null)
  const [conditionPage, setConditionPage] = useState<SurveyPage | null>(null)
  const [showAddPage, setShowAddPage] = useState(false)

  useEffect(() => {
    setExpandedPages(new Set(normalizedPages.map((page) => page.id)))
  }, [normalizedPages])

  const generateId = () => Math.random().toString(36).substr(2, 9)

  const commitPageState = (nextPages: SurveyPage[], nextQuestions = normalizedQuestions) => {
    const normalizedNextState = normalizeQuestionPageState(nextQuestions, nextPages)
    onQuestionsChange(normalizedNextState.questions)
    onPagesChange(normalizedNextState.pages)
  }

  const togglePageExpand = (pageId: string) => {
    setExpandedPages((current) => {
      const next = new Set(current)

      if (next.has(pageId)) {
        next.delete(pageId)
      } else {
        next.add(pageId)
      }

      return next
    })
  }

  const addPage = (title: string, description: string, terminalOutcome?: SurveyPage['terminalOutcome']) => {
    const newPage: SurveyPage = {
      id: generateId(),
      title: title || `Página ${normalizedPages.length + 1}`,
      description,
      questionIds: [],
      terminalOutcome
    }

    commitPageState([...normalizedPages, newPage])
    setShowAddPage(false)
    toast.success('Página criada com sucesso!')
  }

  const updatePage = (pageId: string, updates: Partial<SurveyPage>) => {
    const nextPages = normalizedPages.map((page) => (
      page.id === pageId ? { ...page, ...updates } : page
    ))

    commitPageState(nextPages)
    setEditingPage(null)
    toast.success('Página atualizada!')
  }

  const updatePageBranches = (pageId: string, branches: any[]) => {
    const nextPages = normalizedPages.map((page) => (
      page.id === pageId
        ? { ...page, conditionalBranches: branches.length > 0 ? branches : undefined }
        : page
    ))

    commitPageState(nextPages)
    setBranchPage(null)
    toast.success('Desvios da página atualizados!')
  }

  const updatePageDisplayConditions = (pageId: string, conditions: any[], logic: 'AND' | 'OR') => {
    const nextPages = normalizedPages.map((page) => (
      page.id === pageId
        ? {
          ...page,
          displayConditions: conditions.length > 0 ? conditions : undefined,
          displayLogic: conditions.length > 0 ? logic : undefined
        }
        : page
    ))

    commitPageState(nextPages)
    setConditionPage(null)
    toast.success('Condicoes da página atualizadas!')
  }

  const reorderPage = (pageId: string, targetIndex: number) => {
    const currentIndex = normalizedPages.findIndex((page) => page.id === pageId)
    if (currentIndex < 0) return

    const boundedTargetIndex = Math.min(normalizedPages.length - 1, Math.max(0, targetIndex))
    if (currentIndex === boundedTargetIndex) return

    const nextPages = [...normalizedPages]
    const [removedPage] = nextPages.splice(currentIndex, 1)
    nextPages.splice(boundedTargetIndex, 0, removedPage)
    commitPageState(nextPages)
    toast.success('Ordem das páginas atualizada!')
  }

  const deletePage = (pageId: string) => {
    if (normalizedPages.length === 1) {
      toast.error('Não e possível excluir a ultima página')
      return
    }

    if (!confirm('Tem certeza que deseja excluir esta página? As perguntas serao movidas para outra página.')) {
      return
    }

    const removedPage = normalizedPages.find((page) => page.id === pageId)
    const targetPage = normalizedPages.find((page) => page.id !== pageId)

    if (!removedPage || !targetPage) {
      return
    }

    const nextPages = normalizedPages
      .filter((page) => page.id !== pageId)
      .map((page) => (
        page.id === targetPage.id
          ? { ...page, questionIds: [...page.questionIds, ...removedPage.questionIds] }
          : page
      ))

    commitPageState(nextPages)
    toast.success('Página excluida!')
  }

  const moveQuestionToPage = (questionId: string, targetPageId: string) => {
    const nextPages = normalizedPages.map((page) => {
      const baseIds = page.questionIds.filter((id) => id !== questionId)

      if (page.id !== targetPageId) {
        return {
          ...page,
          questionIds: baseIds
        }
      }

      return {
        ...page,
        questionIds: [...baseIds, questionId]
      }
    })

    commitPageState(nextPages)
    toast.success('Pergunta movida para outra página!')
  }

  const getQuestionsByPage = (pageId: string) => {
    const page = normalizedPages.find((item) => item.id === pageId)
    if (!page) return []

    const questionMap = new Map(normalizedQuestions.map((question) => [question.id, question]))
    return page.questionIds
      .map((questionId) => questionMap.get(questionId))
      .filter((question): question is Question => question !== undefined)
  }

  const getGlobalQuestionNumber = (questionId: string) => {
    return normalizedQuestions.findIndex((question) => question.id === questionId) + 1
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between gap-4 p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3 min-w-0">
            <Layers size={24} className="text-blue-600 shrink-0" />
            <div className="min-w-0">
              <h2 className="text-2xl font-bold text-gray-900">Gerenciar Páginas</h2>
              <p className="text-sm text-gray-500">Organize as perguntas usando a mesma estrutura real da pesquisa.</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2" type="button">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-4">
          <button
            onClick={() => setShowAddPage(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium inline-flex items-center space-x-2 transition-colors"
            type="button"
          >
            <Plus size={20} />
            <span>Nova Página</span>
          </button>

          <div className="space-y-4">
            {normalizedPages.map((page) => {
              const pageQuestions = getQuestionsByPage(page.id)
              const isExpanded = expandedPages.has(page.id)

              return (
                <div key={page.id} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-gray-50 p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <button
                          onClick={() => togglePageExpand(page.id)}
                          className="text-gray-500 hover:text-gray-700 shrink-0"
                          type="button"
                        >
                          {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <div
                              className="survey-rich-text ql-editor min-w-0 font-semibold text-gray-900 [&_*]:inline [&_*]:max-w-full"
                              dangerouslySetInnerHTML={{ __html: page.title || 'Página sem título' }}
                            />
                            <span className="shrink-0 text-sm text-gray-500">({pageQuestions.length} perguntas)</span>
                          </div>
                          {page.description && (
                            <div
                              className="survey-rich-text ql-editor mt-1 text-sm text-gray-600 whitespace-normal break-words [overflow-wrap:anywhere] [&_*]:max-w-full"
                              dangerouslySetInnerHTML={{ __html: page.description }}
                            />
                          )}
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 shrink-0">
                        <button
                          onClick={() => reorderPage(page.id, normalizedPages.findIndex((item) => item.id === page.id) - 1)}
                          disabled={normalizedPages[0]?.id === page.id}
                          className="p-1 text-gray-500 hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-30"
                          title="Mover página para cima"
                          type="button"
                        >
                          <ArrowUp size={16} />
                        </button>
                        <button
                          onClick={() => reorderPage(page.id, normalizedPages.findIndex((item) => item.id === page.id) + 1)}
                          disabled={normalizedPages[normalizedPages.length - 1]?.id === page.id}
                          className="p-1 text-gray-500 hover:text-gray-800 disabled:cursor-not-allowed disabled:opacity-30"
                          title="Mover página para baixo"
                          type="button"
                        >
                          <ArrowDown size={16} />
                        </button>
                        <select
                          value={normalizedPages.findIndex((item) => item.id === page.id)}
                          onChange={(event) => reorderPage(page.id, Number(event.target.value))}
                          className="w-28 rounded border border-gray-300 px-2 py-1 text-xs"
                          title="Mover página para a posicao"
                        >
                          {normalizedPages.map((targetPage, index) => (
                            <option key={targetPage.id} value={index}>
                              Posicao {index + 1}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => setConditionPage(page)}
                          className="relative p-1 text-blue-600 hover:text-blue-800"
                          title="Configurar condicoes de exibicao da página"
                          type="button"
                        >
                          <Filter size={16} />
                          {((page as any).displayConditions?.length || 0) > 0 && (
                            <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-blue-600 px-1 text-[10px] font-semibold text-white">
                              {(page as any).displayConditions?.length}
                            </span>
                          )}
                        </button>
                        <button
                          onClick={() => setBranchPage(page)}
                          className="relative p-1 text-purple-600 hover:text-purple-800"
                          title="Configurar desvios da página"
                          type="button"
                        >
                          <GitBranch size={16} />
                          {(page.conditionalBranches?.length || 0) > 0 && (
                            <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-purple-600 px-1 text-[10px] font-semibold text-white">
                              {page.conditionalBranches?.length}
                            </span>
                          )}
                        </button>
                        <button
                          onClick={() => setEditingPage(page)}
                          className="text-blue-600 hover:text-blue-800 p-1"
                          title="Editar página"
                          type="button"
                        >
                          <Edit2 size={16} />
                        </button>
                        {normalizedPages.length > 1 && (
                          <button
                            onClick={() => deletePage(page.id)}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Excluir página"
                            type="button"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="p-4 space-y-3">
                      {pageQuestions.length === 0 ? (
                        <p className="text-gray-500 text-sm text-center py-4">
                          Nenhuma pergunta nesta página.
                        </p>
                      ) : (
                        pageQuestions.map((question) => (
                          <div key={question.id} className="flex flex-col gap-3 p-3 bg-white border border-gray-200 rounded-lg md:flex-row md:items-start md:justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="mb-1 flex flex-wrap items-center gap-2">
                                <span className="bg-gray-100 text-gray-800 text-xs font-medium px-2 py-1 rounded-full">
                                  #{getGlobalQuestionNumber(question.id)}
                                </span>
                                <span className="font-medium text-gray-900 whitespace-normal break-words [overflow-wrap:anywhere]">
                                  {getQuestionReferenceLabel(question)}
                                </span>
                              </div>
                              {question.conditionalBranches && question.conditionalBranches.length > 0 && (
                                <div className="flex flex-wrap items-center gap-1 text-xs text-purple-600">
                                  <GitBranch size={12} />
                                  <span>{question.conditionalBranches.length} desvio(s) configurado(s)</span>
                                </div>
                              )}
                              {question.displayConditions && question.displayConditions.length > 0 && (
                                <div className="mt-2 rounded-md border border-orange-100 bg-orange-50 px-2 py-1 text-xs text-orange-700">
                                  Exibicao condicionada: {question.displayConditions.length} regra(s)
                                </div>
                              )}
                              {question.conditionalBranches && question.conditionalBranches.length > 0 && (
                                <div className="mt-2 rounded-md border border-purple-100 bg-purple-50 px-2 py-1 text-xs text-purple-700 whitespace-normal break-words [overflow-wrap:anywhere]">
                                  {question.conditionalBranches.map((branch) => {
                                    const optionText = stripHtml(question.options?.find((item) => item.id === branch.optionId)?.text || '')
                                    const targetTitle = stripHtml(normalizedPages.find((item) => item.id === branch.targetPageId)?.title || '')
                                    return `${branch.condition === 'equals' ? 'Se responder' : branch.condition === 'not_equals' ? 'Se não responder' : 'Se contiver'} ${optionText}, ir para ${targetTitle}`
                                  }).join(' • ')}
                                </div>
                              )}
                            </div>

                            <select
                              value={page.id}
                              onChange={(e) => moveQuestionToPage(question.id, e.target.value)}
                              className="w-full text-sm border border-gray-300 rounded px-2 py-1 shrink-0 md:w-auto md:min-w-[180px]"
                            >
                              {normalizedPages.map((targetPage) => (
                                <option key={targetPage.id} value={targetPage.id}>
                                  {stripHtml(targetPage.title) || 'Página sem título'}
                                </option>
                              ))}
                            </select>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        <div className="flex justify-end pt-6 border-t border-gray-200 p-6">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            type="button"
          >
            Concluido
          </button>
        </div>

        {showAddPage && (
          <PageFormModal
            pages={normalizedPages}
            onSave={(title, description, terminalOutcome) => addPage(title, description, terminalOutcome)}
            onClose={() => setShowAddPage(false)}
          />
        )}

        {editingPage && (
          <PageFormModal
            page={editingPage}
            pages={normalizedPages}
            onSave={(title, description, terminalOutcome) => updatePage(editingPage.id, { title, description, terminalOutcome })}
            onClose={() => setEditingPage(null)}
          />
        )}

        {branchPage && (
          <PageBranchModal
            page={branchPage}
            pages={normalizedPages}
            questions={normalizedQuestions}
            onSave={(branches) => updatePageBranches(branchPage.id, branches)}
            onClose={() => setBranchPage(null)}
          />
        )}

        {conditionPage && (
          <PageDisplayConditionModal
            page={conditionPage}
            questions={normalizedQuestions}
            onSave={(conditions, logic) => updatePageDisplayConditions(conditionPage.id, conditions, logic)}
            onClose={() => setConditionPage(null)}
          />
        )}
      </div>
    </div>
  )
}

const PageDisplayConditionModal: React.FC<{
  page: SurveyPage
  questions: Question[]
  onSave: (conditions: any[], logic: 'AND' | 'OR') => void
  onClose: () => void
}> = ({ page, questions, onSave, onClose }) => {
  const [conditions, setConditions] = useState<any[]>((page as any).displayConditions || [])
  const [logic, setLogic] = useState<'AND' | 'OR'>(((page as any).displayLogic || 'AND') as 'AND' | 'OR')

  const addCondition = () => {
    setConditions((previous) => [
      ...previous,
      {
        id: generateBranchId(),
        questionId: '',
        answerPath: undefined,
        operator: 'equals',
        value: '',
        optionId: undefined,
      }
    ])
  }

  const updateCondition = (index: number, updates: Record<string, any>) => {
    setConditions((previous) => previous.map((condition, conditionIndex) => (
      conditionIndex === index ? { ...condition, ...updates } : condition
    )))
  }

  const removeCondition = (index: number) => {
    setConditions((previous) => previous.filter((_, conditionIndex) => conditionIndex !== index))
  }

  const saveConditions = () => {
    const invalid = conditions.some((condition) => (
      !condition.questionId ||
      (!['is_answered', 'not_answered'].includes(condition.operator) && !condition.optionId && String(condition.value ?? '').trim() === '')
    ))

    if (invalid) {
      toast.error('Preencha todas as condicoes da página.')
      return
    }

    onSave(conditions, logic)
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Condicoes da Página</h3>
            <p className="mt-1 text-sm text-gray-500">
              {stripHtml(page.title) || 'Página sem título'} sera exibida quando:
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-6">
          {conditions.length > 1 && (
            <div className="rounded-lg border border-blue-100 bg-blue-50 p-4">
              <label className="mb-2 block text-sm font-medium text-gray-700">Combinar regras usando</label>
              <select
                value={logic}
                onChange={(event) => setLogic(event.target.value as 'AND' | 'OR')}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="AND">E (todas)</option>
                <option value="OR">OU (qualquer)</option>
              </select>
            </div>
          )}

          {conditions.map((condition, index) => {
            const selectedQuestion = questions.find((question) => question.id === condition.questionId)
            const answerPathOptions = getQuestionAnswerPathOptions(selectedQuestion)
            const options = getQuestionOptions(selectedQuestion)

            return (
              <div key={condition.id || index} className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <span className="text-sm font-semibold text-gray-700">Condicao {index + 1}</span>
                  <button type="button" onClick={() => removeCondition(index)} className="rounded-lg p-1 text-red-600 hover:bg-red-50">
                    <Trash2 size={16} />
                  </button>
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-700">Pergunta</label>
                    <select
                      value={condition.questionId || ''}
                      onChange={(event) => updateCondition(index, {
                        questionId: event.target.value,
                        answerPath: undefined,
                        optionId: undefined,
                        value: ''
                      })}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecione...</option>
                      {questions.map((question) => (
                        <option key={question.id} value={question.id}>{getQuestionReferenceLabel(question)}</option>
                      ))}
                    </select>
                  </div>

                  {answerPathOptions.length > 0 && (
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-700">Campo/item</label>
                      <select
                        value={condition.answerPath || ''}
                        onChange={(event) => updateCondition(index, { answerPath: event.target.value || undefined })}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Resposta completa</option>
                        {answerPathOptions.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-700">Operador</label>
                    <select
                      value={condition.operator || 'equals'}
                      onChange={(event) => updateCondition(index, { operator: event.target.value })}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="equals">Igual a</option>
                      <option value="not_equals">Diferente de</option>
                      <option value="contains">Contem</option>
                      <option value="not_contains">Não contem</option>
                      <option value="greater_than">Maior que</option>
                      <option value="less_than">Menor que</option>
                      <option value="is_answered">Foi respondida</option>
                      <option value="not_answered">Não foi respondida</option>
                    </select>
                  </div>

                  {!['is_answered', 'not_answered'].includes(condition.operator) && (
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-700">Valor</label>
                      {options.length > 0 ? (
                        <select
                          value={condition.optionId || ''}
                          onChange={(event) => updateCondition(index, { optionId: event.target.value, value: undefined })}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Selecione...</option>
                          {options.map((option) => (
                            <option key={option.id} value={String(option.value ?? option.id)}>{stripHtml(option.text)}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={condition.value || ''}
                          onChange={(event) => updateCondition(index, { value: event.target.value, optionId: undefined })}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Digite o valor"
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            )
          })}

          <button
            type="button"
            onClick={addCondition}
            className="inline-flex items-center gap-2 rounded-lg border border-blue-200 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50"
          >
            <Plus size={16} />
            Adicionar condicao
          </button>
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4">
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-100">
            Cancelar
          </button>
          <button type="button" onClick={saveConditions} className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700">
            Salvar Condicoes
          </button>
        </div>
      </div>
    </div>
  )
}

const PageFormModal: React.FC<{
  page?: SurveyPage
  pages: SurveyPage[]
  onSave: (title: string, description: string, terminalOutcome?: SurveyPage['terminalOutcome']) => void
  onClose: () => void
}> = ({ page, pages, onSave, onClose }) => {
  const [title, setTitle] = useState(page?.title || '')
  const [description, setDescription] = useState(page?.description || '')
  const [terminalOutcome, setTerminalOutcome] = useState<SurveyPage['terminalOutcome']>(page?.terminalOutcome)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!title.trim()) {
      toast.error('O título da página é obrigatório')
      return
    }

    onSave(title, description, terminalOutcome)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80] p-4">
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">
          {page ? 'Editar Página' : 'Nova Página'}
        </h3>

        <form onSubmit={handleSubmit} className="max-h-[calc(90vh-8rem)] space-y-4 overflow-y-auto pr-1">
          <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-xs text-blue-900">
            Ao criar ou editar uma pagina, informe o <strong>titulo</strong> e, se quiser, a <strong>descricao</strong>.
            A descricao aparece no gerenciador e na impressao do questionario (quando instrucoes estiverem ativas).
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Titulo da Pagina *
            </label>
            <RichTextEditor
              value={title}
              onChange={setTitle}
              placeholder="Ex: Dados Pessoais, Avaliacao, FILTROS, etc."
              height="80px"
            />
          </div>

          <div data-testid="page-description-field">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descricao da pagina
            </label>
            <p className="mb-2 text-xs text-gray-500">
              Texto de apoio exibido aos operadores/respondentes nesta secao (instrucoes da pagina).
            </p>
            <RichTextEditor
              value={description}
              onChange={setDescription}
              placeholder="Ex: Nesta pagina, registre os dados de qualificacao do entrevistado..."
              height="120px"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Resultado ao finalizar nesta página terminal</label>
            <select value={terminalOutcome || ''} onChange={(event) => setTerminalOutcome((event.target.value || undefined) as SurveyPage['terminalOutcome'])} className="w-full rounded-lg border border-gray-300 px-3 py-2">
              <option value="">Concluir normalmente</option>
              <option value="completed">Concluir normalmente</option>
              <option value="cancel_filter">Cancelar por filtro</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">Use em páginas sem perguntas que representam o encerramento da entrevista.</p>
          </div>

          {pages.length > 1 && (
            <div className="rounded-lg border border-blue-100 bg-blue-50 p-4">
              <p className="text-sm font-medium text-blue-900">Ordem da página</p>
              <p className="mt-1 text-xs text-blue-800">
                A posicao tambem pode ser ajustada diretamente na lista usando as setas ou o seletor.
              </p>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {page ? 'Atualizar' : 'Criar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const PageBranchModal: React.FC<{
  page: SurveyPage
  pages: SurveyPage[]
  questions: Question[]
  onSave: (branches: any[]) => void
  onClose: () => void
}> = ({ page, pages, questions, onSave, onClose }) => {
  const selectableQuestions = questions.filter((question) => (
    questionHasOptions(question) || stripHtml(question.title || '').trim().length > 0
  ))
  const [branches, setBranches] = useState<PageBranchDraft[]>(() => (
    ((page as any).conditionalBranches || []).map((branch: any) => ({
      id: String(branch.id || generateBranchId()),
      targetPageId: String(branch.targetPageId || ''),
      targetQuestionId: String(branch.targetQuestionId || ''),
      targetType: branch.targetType || (branch.targetQuestionId ? 'question' : 'page'),
      conditions: (Array.isArray(branch.conditions) && branch.conditions.length > 0
        ? branch.conditions
        : []
      ).map((condition: any, index: number) => ({
        questionId: String(condition.questionId || ''),
        answerPath: String(condition.answerPath || condition.itemId || condition.rowId || condition.fieldId || '') || undefined,
        answerValue: String(condition.value ?? condition.optionId ?? ''),
        comparison: (String(condition.operator || 'equals') as PageBranchComparisonOperator),
        operator: index === 0 ? undefined : (String(condition.logicalOperator || branch.logic || 'AND') as 'AND' | 'OR')
      })),
      isMultiple: Array.isArray(branch.conditions) && branch.conditions.length > 1,
      unconditional: !Array.isArray(branch.conditions) || branch.conditions.length === 0
    }))
  ))
  const [editingBranch, setEditingBranch] = useState<PageBranchDraft | null>(null)

  const getQuestionLabel = (questionId: string) => (
    stripHtml(questions.find((question) => question.id === questionId)?.title || '') || 'Pergunta sem título'
  )

  const getBranchQuestionLabel = (question: Question) => (
    question.variable?.trim() || stripHtml(question.title || '') || 'Pergunta sem título'
  )

  // Perguntas de qualquer pagina (mesma pagina ou outra) podem ser destino do desvio.
  const branchTargetQuestionOptions = useMemo(() => {
    const options: Array<{ questionId: string; label: string }> = []
    const seen = new Set<string>()

    pages.forEach((targetPage) => {
      ;(targetPage.questionIds || []).forEach((questionId) => {
        if (seen.has(questionId)) return
        const question = questions.find((item) => item.id === questionId)
        if (!question) return
        seen.add(questionId)
        const pageLabel = stripHtml(targetPage.title) || 'Página sem título'
        options.push({
          questionId,
          label: `${pageLabel} — ${getBranchQuestionLabel(question)}`
        })
      })
    })

    questions.forEach((question) => {
      if (seen.has(question.id)) return
      seen.add(question.id)
      options.push({ questionId: question.id, label: getBranchQuestionLabel(question) })
    })

    return options
  }, [pages, questions])

  const beginNewBranch = () => {
    const firstQuestionId = selectableQuestions[0]?.id || ''
    setEditingBranch({
      id: generateBranchId(),
      targetPageId: '',
      targetQuestionId: '',
      targetType: 'page',
      conditions: [{
        questionId: firstQuestionId,
        answerValue: '',
        comparison: 'equals'
      }],
      isMultiple: false,
      unconditional: true
    })
  }

  const updateEditingBranch = (updates: Partial<PageBranchDraft>) => {
    setEditingBranch((previous) => previous ? { ...previous, ...updates } : previous)
  }

  const updateCondition = (index: number, updates: Partial<PageBranchConditionDraft>) => {
    setEditingBranch((previous) => {
      if (!previous) return previous
      const nextConditions = [...previous.conditions]
      nextConditions[index] = { ...nextConditions[index], ...updates }
      return { ...previous, conditions: nextConditions }
    })
  }

  const addCondition = () => {
    setEditingBranch((previous) => {
      if (!previous) return previous
      return {
        ...previous,
        isMultiple: true,
        conditions: [
          ...previous.conditions,
          {
            questionId: selectableQuestions[0]?.id || '',
            answerValue: '',
            comparison: 'equals',
            operator: 'AND'
          }
        ]
      }
    })
  }

  const removeCondition = (index: number) => {
    setEditingBranch((previous) => {
      if (!previous || previous.conditions.length <= 1) return previous
      const nextConditions = previous.conditions.filter((_, conditionIndex) => conditionIndex !== index)
      return {
        ...previous,
        conditions: nextConditions,
        isMultiple: nextConditions.length > 1
      }
    })
  }

  const saveEditingBranch = () => {
    if (!editingBranch) return

    if (editingBranch.targetType === 'page' && !editingBranch.targetPageId) {
      toast.error('Selecione o destino do desvio.')
      return
    }

    if (editingBranch.targetType === 'question' && !editingBranch.targetQuestionId) {
      toast.error('Selecione a pergunta de destino do desvio.')
      return
    }

    if (editingBranch.targetType === 'page' && editingBranch.targetPageId === page.id) {
      toast.error('A página de destino precisa ser diferente da página atual.')
      return
    }

    if (!editingBranch.unconditional && editingBranch.conditions.some((condition) => (
      !condition.questionId ||
      (!condition.answerValue && !isPresenceComparison(condition.comparison))
    ))) {
      toast.error('Selecione a pergunta e a resposta de cada condicao.')
      return
    }

    setBranches((previous) => {
      const existingIndex = previous.findIndex((branch) => branch.id === editingBranch.id)
      if (existingIndex >= 0) {
        const nextBranches = [...previous]
        nextBranches[existingIndex] = editingBranch
        return nextBranches
      }

      return [...previous, editingBranch]
    })
    setEditingBranch(null)
  }

  const applyBranches = () => {
    const mappedBranches = branches.map((branch) => ({
      id: branch.id,
      targetType: branch.targetType,
      targetPageId: branch.targetType === 'page' ? branch.targetPageId : undefined,
      targetQuestionId: branch.targetType === 'question' ? branch.targetQuestionId : undefined,
      logic: branch.isMultiple
        ? (branch.conditions.find((condition, index) => index > 0 && condition.operator)?.operator || 'AND')
        : 'AND',
      conditions: branch.unconditional ? [] : branch.conditions.map((condition, conditionIndex) => ({
        questionId: condition.questionId,
        answerPath: condition.answerPath || undefined,
        operator: condition.comparison || 'equals',
        logicalOperator: conditionIndex === 0 ? undefined : (condition.operator || 'AND'),
        value: condition.answerValue
      }))
    }))

    onSave(mappedBranches)
  }

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/50 p-4"
      onClick={(event) => event.target === event.currentTarget && onClose()}
    >
      <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 bg-purple-50 px-6 py-4">
          <div className="min-w-0">
            <h3 className="text-xl font-semibold text-gray-900">Desvios da Página</h3>
            <p className="mt-1 break-words text-sm text-gray-600">
              {stripHtml(page.title) || 'Página sem título'}
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-gray-400 hover:bg-white hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {!editingBranch ? (
            <div className="space-y-4">
              <button
                type="button"
                onClick={beginNewBranch}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-purple-600 px-4 py-3 text-white transition-colors hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Plus size={18} />
                <span>Novo Desvio de Página</span>
              </button>

              {branches.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-6 py-12 text-center text-gray-500">
                  Nenhum desvio de página configurado.
                </div>
              ) : (
                <div className="space-y-3">
                  {branches.map((branch) => {
                    const targetPageTitle = stripHtml(pages.find((item) => item.id === branch.targetPageId)?.title || '') || 'Página não encontrada'
                    return (
                      <div key={branch.id} className="rounded-xl border border-gray-200 bg-white p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0 flex-1">
                            <div className="mb-2 flex flex-wrap items-center gap-2">
                              <span className="rounded-full bg-purple-100 px-2.5 py-1 text-xs font-medium text-purple-700">
                                {branch.unconditional ? 'Sempre ao terminar a página' : branch.isMultiple ? 'Multiplo' : 'Simples'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700">
                              <strong>Ir para:</strong> {branch.targetType === 'complete'
                                ? 'Finalizar questionário'
                                : branch.targetType === 'cancel_filter'
                                  ? 'Cancelar por filtro'
                                  : branch.targetType === 'question'
                                    ? `Pergunta: ${branchTargetQuestionOptions.find((item) => item.questionId === branch.targetQuestionId)?.label || 'Pergunta não encontrada'}`
                                    : targetPageTitle}
                            </p>
                          </div>

                          <div className="flex shrink-0 items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setEditingBranch({
                                ...branch,
                                conditions: branch.conditions.map((condition) => ({ ...condition }))
                              })}
                              className="rounded-lg p-2 text-blue-600 transition-colors hover:bg-blue-50"
                              title="Editar desvio"
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              type="button"
                              onClick={() => setBranches((previous) => previous.filter((item) => item.id !== branch.id))}
                              className="rounded-lg p-2 text-red-600 transition-colors hover:bg-red-50"
                              title="Excluir desvio"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Destino do desvio
                </label>
                <select
                  value={
                    editingBranch.targetType === 'page'
                      ? `page:${editingBranch.targetPageId}`
                      : editingBranch.targetType === 'question'
                        ? `question:${editingBranch.targetQuestionId}`
                        : editingBranch.targetType
                  }
                  onChange={(event) => {
                    const value = event.target.value
                    if (value.startsWith('page:')) {
                      updateEditingBranch({ targetType: 'page', targetPageId: value.slice(5), targetQuestionId: '' })
                    } else if (value.startsWith('question:')) {
                      updateEditingBranch({ targetType: 'question', targetQuestionId: value.slice('question:'.length), targetPageId: '' })
                    } else {
                      updateEditingBranch({ targetType: value as 'complete' | 'cancel_filter', targetPageId: '', targetQuestionId: '' })
                    }
                  }}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Selecione...</option>
                  <option value="complete">Finalizar questionário</option>
                  <option value="cancel_filter">Cancelar por filtro</option>
                  <optgroup label="Perguntas (mesma página ou outra)">
                    {branchTargetQuestionOptions.map((item) => (
                      <option key={item.questionId} value={`question:${item.questionId}`}>
                        {item.label}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="Páginas">
                    {pages
                      .filter((item) => item.id !== page.id)
                      .map((targetPage) => (
                        <option key={targetPage.id} value={`page:${targetPage.id}`}>
                          {stripHtml(targetPage.title) || 'Página sem título'}
                        </option>
                      ))}
                  </optgroup>
                </select>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-800">Pergunta de condição</h4>
                    <p className="mt-1 text-xs text-gray-500">
                      Escolha a pergunta e a resposta que determinarão o desvio.
                    </p>
                  </div>
                  {editingBranch.isMultiple && editingBranch.conditions.length > 1 && (
                    <span className="text-xs font-medium text-purple-700">Múltiplas condições</span>
                  )}
                </div>

                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={editingBranch.unconditional} onChange={(event) => updateEditingBranch({ unconditional: event.target.checked })} />
                  Aplicar sempre ao concluir esta página
                </label>

                {!editingBranch.unconditional && editingBranch.conditions.map((condition, index) => {
                  const selectedQuestion = questions.find((question) => question.id === condition.questionId)
                  const answerPathOptions = getQuestionAnswerPathOptions(selectedQuestion)
                  const valueOptions = getQuestionAnswerValueOptions(selectedQuestion, condition.answerPath)

                  return (
                    <div key={`${editingBranch.id}_condition_${index}`} className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                      {index > 0 && editingBranch.isMultiple && (
                        <div className="mb-3">
                          <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-gray-500">
                            Operador (combinar com a condicao anterior)
                          </label>
                          <select
                            value={condition.operator || 'AND'}
                            onChange={(event) => updateCondition(index, { operator: event.target.value as 'AND' | 'OR' })}
                            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="AND">E (AND)</option>
                            <option value="OR">Ou (OR)</option>
                          </select>
                        </div>
                      )}

                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">Pergunta</label>
                          <select
                            value={condition.questionId || ''}
                            onChange={(event) => updateCondition(index, {
                              questionId: event.target.value,
                              answerPath: undefined,
                              answerValue: ''
                            })}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="">Selecione...</option>
                            {selectableQuestions.map((question) => (
                              <option key={question.id} value={question.id}>
                                {getBranchQuestionLabel(question)}
                              </option>
                            ))}
                          </select>
                        </div>

                        {answerPathOptions.length > 0 && (
                          <div>
                            <label className="mb-1 block text-sm font-medium text-gray-700">Campo/item</label>
                            <select
                              value={condition.answerPath || ''}
                              onChange={(event) => updateCondition(index, {
                                answerPath: event.target.value || undefined,
                                answerValue: ''
                              })}
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-purple-500"
                            >
                              <option value="">Resposta completa</option>
                              {answerPathOptions.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          </div>
                        )}

                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">Condicao da resposta</label>
                          <select
                            value={condition.comparison || 'equals'}
                            onChange={(event) => updateCondition(index, {
                              comparison: event.target.value as PageBranchComparisonOperator,
                              answerValue: isPresenceComparison(event.target.value) ? '' : condition.answerValue
                            })}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-purple-500"
                          >
                            {PAGE_BRANCH_COMPARISON_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </select>
                        </div>

                        {!isPresenceComparison(condition.comparison) && (
                        <div>
                          <label className="mb-1 block text-sm font-medium text-gray-700">Resposta</label>
                          {valueOptions.length > 0 ? (
                            <select
                              value={condition.answerValue || ''}
                              onChange={(event) => updateCondition(index, { answerValue: event.target.value })}
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-purple-500"
                            >
                              <option value="">Selecione...</option>
                              {valueOptions.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type="text"
                              value={condition.answerValue || ''}
                              onChange={(event) => updateCondition(index, { answerValue: event.target.value })}
                              placeholder={selectedQuestion ? 'Digite o valor da resposta' : 'Selecione uma pergunta primeiro'}
                              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                          )}
                        </div>
                        )}
                      </div>

                      {editingBranch.isMultiple && editingBranch.conditions.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeCondition(index)}
                          className="mt-3 text-sm font-medium text-red-600 hover:text-red-700"
                        >
                          Remover condição
                        </button>
                      )}
                    </div>
                  )
                })}

                {!editingBranch.unconditional && <button
                  type="button"
                  onClick={addCondition}
                  className="inline-flex items-center gap-2 text-sm font-medium text-purple-700 hover:text-purple-900"
                >
                  <Plus size={15} />
                  Adicionar condição
                </button>}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setEditingBranch(null)}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={saveEditingBranch}
                  className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700"
                >
                  <GitBranch size={16} />
                  Salvar Desvio
                </button>
              </div>
            </div>
          )}
        </div>

        {!editingBranch && (
          <div className="flex justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-100"
            >
              Fechar
            </button>
            <button
              type="button"
              onClick={applyBranches}
              className="rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700"
            >
              Aplicar Desvios
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default PageManager